/**
 * Typed client for the remediation screens (track T5). Every call goes through the BFF to
 * `/v1/audit-remediation/*` and every response is parsed with the contract schema, so a drifting API fails loudly
 * instead of rendering garbage. APIs are built in parallel (T6 engine, T7-T9 providers, T11 security/monitoring);
 * shapes not yet in `@qm/contracts/audit-remediation` are defined here (LOCAL) and listed in docs/status/aa-t5.md
 * as integration requests so F0 can move them into the contract.
 *
 * Approve-first is enforced by the API: an approve / bulk approve / rollback may answer 403 with
 * `details.reason = 'step_up_required'`; the UI then asks for the second factor and retries (isStepUpRequired).
 */
import { z } from 'zod';
import { GradeSchema } from '@qm/contracts/audit-analysis';
import {
  ACTOR_TYPES, AdvancedReportSchema, CreateProposalRequestSchema, REMEDIATION_EVENT_KINDS, MonitorAlertSchema, MonitorRunSchema, MonitorScheduleSchema,
  ProposalDecisionRequestSchema, ProviderConnectionSchema, RefIdSchema, RemediationEventSchema, RemediationProposalSchema,
  SecuritySettingsSchema, UpdateSecuritySettingsRequestSchema, UpsertMonitorScheduleRequestSchema,
  type AdvancedReport, type CreateProposalRequest, type MonitorAlert, type MonitorRun, type MonitorSchedule, type ProposalDecisionRequest,
  type ProviderConnection, type RemediationEvent, type RemediationProposal, type RemediationProviderId, type SecuritySettings,
  type UpdateSecuritySettingsRequest, type UpsertMonitorScheduleRequest,
} from '@qm/contracts/audit-remediation';
import { bff, BffError } from '../../../../../lib/bff';

export const API = '/v1/audit-remediation' as const;


// ---------- LOCAL shapes (wire formats of the T6 / T7-T9 / T11 routes; contract move is an F0 follow-up) ----------
/** GET /engine/proposals -> every proposal of the tenant, newest first (pending + history). */
export const ListProposalsResponseSchema = z.object({ data: z.array(RemediationProposalSchema) });
/** POST /engine/proposals/bulk-approve: low-risk proposals only, one step-up for the batch (T6). */
export const BulkApproveRequestSchema = z.object({ proposalIds: z.array(z.string().min(1).max(64)).min(1).max(50), note: z.string().max(1000).optional() });
export type BulkApproveRequest = z.infer<typeof BulkApproveRequestSchema>;
export const BulkApproveResponseSchema = z.object({ approved: z.array(z.string()), skipped: z.array(z.object({ id: z.string(), reason: z.string() })) });
/** GET /engine/findings/:findingId/assist?jobId= -> generated content / code for an `assisted` finding. */
export const AssistBlockSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(200),
  kind: z.enum(['text', 'html', 'code', 'json', 'dns', 'header']),
  content: z.string().min(1).max(50_000),
  /** where to paste it, plain words */
  where: z.string().max(500).nullable().default(null),
});
export const AssistedFixSchema = z.object({ findingId: RefIdSchema, blocks: z.array(AssistBlockSchema).min(1).max(20), generatedAt: z.string() });
export type AssistedFix = z.infer<typeof AssistedFixSchema>;
/** POST /engine/findings/:findingId/retest { jobId } -> re-test request (verified by the next monitoring run). */
export const RetestRequestSchema = z.object({ jobId: RefIdSchema });
export const RetestResultSchema = z.object({ status: z.enum(['queued', 'passed', 'failed', 'not_measured']), checkedAt: z.string().nullable(), reason: z.string().max(500).nullable().default(null) });
export type RetestResult = z.infer<typeof RetestResultSchema>;
/** POST /v1/identity/step-up/totp { code } (identity, T11) -> fresh second factor. */
export const StepUpRequestSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
export const StepUpResponseSchema = z.object({ verifiedAt: z.string() });
/** POST /providers/<p>/connect bodies (T7 / T9 / T8). Secrets go straight to the vault; they are never echoed back. */
export const WordPressConnectSchema = z.object({
  siteUrl: z.string().url().max(500), username: z.string().min(1).max(200), applicationPassword: z.string().min(16).max(200),
  /** Settings > Quixo Connector signing secret (64 hex) */
  signingSecret: z.string().regex(/^[a-fA-F0-9]{64}$/),
});
export const CloudflareConnectSchema = z.object({ token: z.string().min(20).max(200), domain: z.string().min(3).max(253), dnsEmailRecords: z.boolean().optional() });
/** GBP / Meta reuse the tenant's connected Google / Meta account (connectors) and pick the location / Page. */
export const GbpConnectSchema = z.object({ sourceConnectionId: z.string().min(1).max(64), locationName: z.string().min(1).max(200) });
export const MetaConnectSchema = z.object({ sourceConnectionId: z.string().min(1).max(64), pageId: z.string().min(1).max(64), pixelId: z.string().min(1).max(64).optional() });
export const ConnectResultSchema = z.object({ connection: ProviderConnectionSchema.optional(), authorizeUrl: z.string().url().optional() });
export type ConnectResult = z.infer<typeof ConnectResultSchema>;
export type ConnectBody =
  | { provider: 'wordpress'; body: z.infer<typeof WordPressConnectSchema> }
  | { provider: 'cloudflare'; body: z.infer<typeof CloudflareConnectSchema> }
  | { provider: 'gbp'; body: z.infer<typeof GbpConnectSchema> }
  | { provider: 'meta'; body: z.infer<typeof MetaConnectSchema> };
/** Where a customer connects the Google / Meta account the GBP / Meta providers reuse (connectors, OAuth). */
export const OAUTH_CONNECT_PATH = '/integrations';
/** GET /monitoring/trend?scheduleId= -> score per finished run (oldest first). */
export const TrendPointSchema = z.object({ at: z.string(), score: z.number().int().min(0).max(100) });
export const MonitorTrendSchema = z.object({ points: z.array(TrendPointSchema).max(400) });
export type TrendPoint = z.infer<typeof TrendPointSchema>;
const ApiTrendPointSchema = z.object({ at: z.string(), score: z.number().int().min(0).max(100).nullable() });
/** GET /security/audit-trail?source=remediation (T11) rows; mapped to the contract RemediationEvent. */
const TrailEntrySchema = z.object({
  id: z.string(), source: z.enum(['finding', 'remediation']), kind: z.string(), at: z.string(), actorId: z.string(), actorType: z.string(),
  jobId: z.string().nullable(), findingId: z.string().nullable(), proposalId: z.string().nullable(), detail: z.record(z.string(), z.unknown()),
});
/** GET /security/summary?jobId= -> security grade of a run (T11). measured=false -> not_measured (rule 2). */
export const SecuritySummarySchema = z.object({
  jobId: RefIdSchema,
  grade: GradeSchema,
  score: z.number().int().min(0).max(100).nullable(),
  checksRun: z.number().int().min(0),
  notMeasured: z.array(z.object({ checkId: z.string(), reason: z.string() })).max(200).default([]),
});
export type SecuritySummary = z.infer<typeof SecuritySummarySchema>;

// ---------- client ----------
export interface RemediationClient {
  report(jobId: string): Promise<AdvancedReport>;
  connections(): Promise<ProviderConnection[]>;
  proposals(): Promise<RemediationProposal[]>;
  proposal(id: string): Promise<RemediationProposal>;
  propose(req: CreateProposalRequest): Promise<RemediationProposal>;
  decide(id: string, req: ProposalDecisionRequest): Promise<RemediationProposal>;
  bulkApprove(ids: string[]): Promise<RemediationProposal[]>;
  rollback(id: string): Promise<RemediationProposal>;
  assist(jobId: string, findingId: string): Promise<AssistedFix>;
  retest(jobId: string, findingId: string): Promise<RetestResult>;
  stepUp(code: string): Promise<void>;
  connect(req: ConnectBody): Promise<ConnectResult>;
  testConnection(provider: RemediationProviderId, id: string): Promise<ProviderConnection>;
  disconnect(provider: RemediationProviderId, id: string): Promise<ProviderConnection>;
  schedule(): Promise<MonitorSchedule | null>;
  saveSchedule(req: UpsertMonitorScheduleRequest): Promise<MonitorSchedule>;
  runs(): Promise<MonitorRun[]>;
  alerts(): Promise<MonitorAlert[]>;
  ackAlert(id: string): Promise<MonitorAlert>;
  trend(): Promise<TrendPoint[]>;
  securitySettings(): Promise<SecuritySettings>;
  saveSecuritySettings(req: UpdateSecuritySettingsRequest): Promise<SecuritySettings>;
  securitySummary(jobId: string): Promise<SecuritySummary>;
  events(): Promise<RemediationEvent[]>;
}

/** Endpoint table (method + path + schema): the routes T6 (engine), T7-T9 (providers), T11 (security, monitoring) serve. */
export const ENDPOINTS = {
  report: ['GET', '/jobs/:jobId/report', 'GetAdvancedReportResponseSchema'],
  connections: ['GET', '/providers/connections', 'ListProviderConnectionsResponseSchema'],
  proposals: ['GET', '/engine/proposals', 'ListProposalsResponseSchema'],
  proposal: ['GET', '/engine/proposals/:id', 'ProposalResponseSchema'],
  propose: ['POST', '/engine/proposals', 'CreateProposalRequestSchema -> ProposalResponseSchema'],
  decide: ['POST', '/engine/proposals/:id/decision', 'ProposalDecisionRequestSchema -> ProposalResponseSchema'],
  bulkApprove: ['POST', '/engine/proposals/bulk-approve', 'BulkApproveRequestSchema -> { approved[], skipped[] }, then GET each'],
  rollback: ['POST', '/engine/proposals/:id/rollback', '{} -> ProposalResponseSchema'],
  assist: ['GET', '/engine/findings/:findingId/assist?jobId=', 'AssistedFixSchema'],
  retest: ['POST', '/engine/findings/:findingId/retest', 'RetestRequestSchema -> RetestResultSchema'],
  stepUp: ['POST', '/v1/identity/step-up/totp', 'StepUpRequestSchema -> StepUpResponseSchema'],
  connect: ['POST', '/providers/:provider/connect', 'WordPress|Cloudflare|Gbp|MetaConnectSchema -> { connection }'],
  testConnection: ['POST', '/providers/wordpress/connections/:id/check (others: GET /providers/connections)', 'ProviderConnectionSchema'],
  disconnect: ['POST', '/providers/wordpress/connections/:id/disconnect | /providers/:provider/disconnect', 'ProviderConnectionSchema'],
  schedule: ['GET', '/monitoring/schedules', 'MonitorScheduleSchema[] (first) | null'],
  saveSchedule: ['PUT', '/monitoring/schedules', 'UpsertMonitorScheduleRequestSchema -> MonitorScheduleSchema'],
  runs: ['GET', '/monitoring/runs', 'MonitorRunSchema[]'],
  alerts: ['GET', '/monitoring/alerts', 'MonitorAlertSchema[]'],
  ackAlert: ['POST', '/monitoring/alerts/:id/acknowledge', 'MonitorAlertSchema'],
  trend: ['GET', '/monitoring/trend?scheduleId=', 'trend points'],
  securitySettings: ['GET', '/security/settings', 'SecuritySettingsSchema'],
  saveSecuritySettings: ['PUT', '/security/settings', 'UpdateSecuritySettingsRequestSchema -> SecuritySettingsSchema'],
  securitySummary: ['GET', '/security/summary?jobId=', 'SecuritySummarySchema'],
  events: ['GET', '/security/audit-trail?source=remediation', 'trail rows -> RemediationEventSchema[]'],
} as const satisfies Record<keyof RemediationClient, readonly [string, string, string]>;

type Path = `/v1/${string}`;
const enc = encodeURIComponent;
const p = (rest: string): Path => `${API}${rest}`;
const KINDS = new Set<string>(REMEDIATION_EVENT_KINDS);
const ACTORS = new Set<string>(ACTOR_TYPES);

/** Production client over the BFF. `call` is injectable for tests. */
export function bffRemediationClient(call: <T>(path: Path, init?: RequestInit) => Promise<T> = bff): RemediationClient {
  const get = async <S extends z.ZodTypeAny>(path: Path, schema: S): Promise<z.infer<S>> => schema.parse(await call<unknown>(path));
  const send = async <S extends z.ZodTypeAny>(path: Path, method: string, body: unknown, schema: S): Promise<z.infer<S>> =>
    schema.parse(await call<unknown>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) }));
  const list = <S extends z.ZodTypeAny>(s: S) => z.array(s);
  const withConnection = z.object({ connection: ProviderConnectionSchema });
  const proposal = (id: string) => get(p(`/engine/proposals/${enc(id)}`), RemediationProposalSchema);
  const schedules = () => get(p('/monitoring/schedules'), list(MonitorScheduleSchema));
  return {
    report: (jobId) => get(p(`/jobs/${enc(jobId)}/report`), AdvancedReportSchema),
    connections: () => get(p('/providers/connections'), list(ProviderConnectionSchema)),
    proposals: () => get(p('/engine/proposals'), ListProposalsResponseSchema.shape.data),
    proposal,
    propose: (req) => send(p('/engine/proposals'), 'POST', CreateProposalRequestSchema.parse(req), RemediationProposalSchema),
    decide: (id, req) => send(p(`/engine/proposals/${enc(id)}/decision`), 'POST', ProposalDecisionRequestSchema.parse(req), RemediationProposalSchema),
    bulkApprove: async (ids) => {
      const r = await send(p('/engine/proposals/bulk-approve'), 'POST', BulkApproveRequestSchema.parse({ proposalIds: ids }), BulkApproveResponseSchema);
      return Promise.all(r.approved.map((id) => proposal(id)));
    },
    rollback: (id) => send(p(`/engine/proposals/${enc(id)}/rollback`), 'POST', {}, RemediationProposalSchema),
    assist: (jobId, findingId) => get(p(`/engine/findings/${enc(findingId)}/assist?jobId=${enc(jobId)}`), AssistedFixSchema),
    retest: (jobId, findingId) => send(p(`/engine/findings/${enc(findingId)}/retest`), 'POST', RetestRequestSchema.parse({ jobId }), RetestResultSchema),
    stepUp: async (code) => { await send('/v1/identity/step-up/totp', 'POST', StepUpRequestSchema.parse({ code }), StepUpResponseSchema); },
    connect: async (req) => {
      if (req.provider === 'wordpress') return { connection: (await send(p('/providers/wordpress/connect'), 'POST', WordPressConnectSchema.parse(req.body), withConnection)).connection };
      if (req.provider === 'cloudflare') return { connection: (await send(p('/providers/cloudflare/connect'), 'POST', CloudflareConnectSchema.parse(req.body), withConnection)).connection };
      if (req.provider === 'gbp') return { connection: await send(p('/providers/gbp/connect'), 'POST', GbpConnectSchema.parse(req.body), ProviderConnectionSchema) };
      return { connection: await send(p('/providers/meta/connect'), 'POST', MetaConnectSchema.parse(req.body), ProviderConnectionSchema) };
    },
    testConnection: async (provider, id) => {
      if (provider === 'wordpress') return (await send(p(`/providers/wordpress/connections/${enc(id)}/check`), 'POST', {}, withConnection)).connection;
      const found = (await get(p('/providers/connections'), list(ProviderConnectionSchema))).find((c) => c.id === id);
      if (!found) throw new BffError(404, null, false);
      return found;
    },
    disconnect: async (provider, id) => {
      if (provider === 'wordpress') return (await send(p(`/providers/wordpress/connections/${enc(id)}/disconnect`), 'POST', {}, withConnection)).connection;
      if (provider === 'cloudflare') return (await send(p('/providers/cloudflare/disconnect'), 'POST', { connectionId: id }, withConnection)).connection;
      return send(p(`/providers/${provider}/disconnect`), 'POST', { connectionId: id }, ProviderConnectionSchema);
    },
    schedule: async () => (await schedules())[0] ?? null,
    saveSchedule: (req) => send(p('/monitoring/schedules'), 'PUT', UpsertMonitorScheduleRequestSchema.parse(req), MonitorScheduleSchema),
    runs: () => get(p('/monitoring/runs'), list(MonitorRunSchema)),
    alerts: () => get(p('/monitoring/alerts'), list(MonitorAlertSchema)),
    ackAlert: (id) => send(p(`/monitoring/alerts/${enc(id)}/acknowledge`), 'POST', {}, MonitorAlertSchema),
    trend: async () => {
      const s = (await schedules())[0];
      if (!s) return [];
      const points = await get(p(`/monitoring/trend?scheduleId=${enc(s.id)}`), list(ApiTrendPointSchema));
      return points.flatMap((x) => (x.score === null ? [] : [{ at: x.at, score: x.score }]));
    },
    securitySettings: () => get(p('/security/settings'), SecuritySettingsSchema),
    saveSecuritySettings: (req) => send(p('/security/settings'), 'PUT', UpdateSecuritySettingsRequestSchema.parse(req), SecuritySettingsSchema),
    securitySummary: (jobId) => get(p(`/security/summary?jobId=${enc(jobId)}`), SecuritySummarySchema),
    events: async () => {
      const rows = await get(p('/security/audit-trail?source=remediation&limit=100'), list(TrailEntrySchema));
      return rows.flatMap((r) => (r.proposalId && r.findingId && KINDS.has(r.kind) ? [RemediationEventSchema.parse({
        id: r.id, proposalId: r.proposalId, findingId: r.findingId, kind: r.kind, actorId: r.actorId,
        actorType: ACTORS.has(r.actorType) ? r.actorType : 'system', detail: r.detail, at: r.at,
      })] : []));
    },
  };
}

// ---------- error classification ----------
const details = (e: unknown): Record<string, unknown> => {
  const d = e instanceof BffError ? e.envelope?.error.details : null;
  return d && typeof d === 'object' ? (d as Record<string, unknown>) : {};
};

/** 403 with reason step_up_required: ask for the second factor, then retry the same action. */
export const isStepUpRequired = (e: unknown): boolean => e instanceof BffError && e.status === 403 && details(e).reason === 'step_up_required';

export type LoadFailure = 'flag_off' | 'forbidden' | 'not_found' | 'error';
/** How a screen should render a failed load: flag off (404 feature_disabled), forbidden (403), not found, or a generic error. */
export function failureOf(e: unknown): LoadFailure {
  if (e instanceof BffError) {
    if (e.status === 404 && e.envelope?.error.code === 'feature_disabled') return 'flag_off';
    if (e.status === 403) return 'forbidden';
    if (e.status === 404) return 'not_found';
  }
  return 'error';
}

/** i18n key for a failed action (approve, reject, rollback, connect, save). */
export function actionErrorKey(e: unknown): string {
  if (isStepUpRequired(e)) return 'audit-remediation.errors.stepUpRequired';
  if (e instanceof BffError) {
    const key = details(e).messageKey;
    if (typeof key === 'string' && key.startsWith('audit-remediation.errors.')) return key;
    if (e.status === 403) return 'audit-remediation.actions.errors.forbidden';
    if (e.status === 404 && e.envelope?.error.code === 'feature_disabled') return 'audit-remediation.actions.errors.flagOff';
    if (e.status === 409) return 'audit-remediation.actions.errors.conflict';
    if (e.status === 400) return 'audit-remediation.actions.errors.invalid';
  }
  return 'audit-remediation.actions.errors.generic';
}
